import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import AddProblemForm from './components/AddProblemForm';
import Filters from './components/Filters';
import ProblemCard from './components/ProblemCard';
import EmptyState from './components/EmptyState';
import { calculateStreak, getAchievementLevel } from './utils/helpers';
import { INITIAL_FORM_DATA, PLATFORMS } from './utils/constants';
import './App.css';

function App() {
  // Load data from localStorage on initial render
  const [questions, setQuestions] = useState(() => {
    const savedQuestions = localStorage.getItem('dsaTrackerQuestions');
    return savedQuestions ? JSON.parse(savedQuestions) : [];
  });
  
  const [showForm, setShowForm] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [filter, setFilter] = useState('all');

  // Save to localStorage whenever questions change
  useEffect(() => {
    localStorage.setItem('dsaTrackerQuestions', JSON.stringify(questions));
  }, [questions]);

  const streak = calculateStreak(questions);
  const achievement = getAchievementLevel(
    questions.filter((q) => q.attempted).length
  );

  const stats = {
    total: questions.length,
    attempted: questions.filter((q) => q.attempted).length,
    notAttempted: questions.filter((q) => !q.attempted).length,
    starred: questions.filter((q) => q.starred).length,
    easy: questions.filter((q) => q.difficulty === 'Easy').length,
    medium: questions.filter((q) => q.difficulty === 'Medium').length,
    hard: questions.filter((q) => q.difficulty === 'Hard').length,
  };

  const addQuestion = () => {
    if (!formData.title.trim()) {
      alert('Please enter a problem title!');
      return;
    }
    if (!formData.algorithm) {
      alert('Please select an algorithm/pattern!');
      return;
    }
    if (!formData.solution.trim()) {
      alert('Please enter your solution!');
      return;
    }

    const newQuestion = {
      id: Date.now(),
      ...formData,
      attempted: true,
      date: new Date().toLocaleDateString(),
      timestamp: new Date().toISOString(),
    };

    setQuestions([newQuestion, ...questions]);
    setFormData(INITIAL_FORM_DATA);
    setShowForm(false);
  };

  const toggleAttempted = (id) => {
    setQuestions(
      questions.map((q) =>
        q.id === id ? { ...q, attempted: !q.attempted } : q
      )
    );
  };

  const toggleStarred = (id) => {
    setQuestions(
      questions.map((q) => (q.id === id ? { ...q, starred: !q.starred } : q))
    );
  };

  const deleteQuestion = (id) => {
    if (window.confirm('Are you sure you want to delete this problem?')) {
      setQuestions(questions.filter((q) => q.id !== id));
    }
  };

  const clearAllData = () => {
    if (window.confirm('Are you sure you want to delete ALL problems? This cannot be undone!')) {
      setQuestions([]);
      localStorage.removeItem('dsaTrackerQuestions');
    }
  };

  const filteredQuestions = questions.filter((q) => {
    if (filter === 'all') return true;
    if (filter === 'attempted') return q.attempted;
    if (filter === 'not-attempted') return !q.attempted;
    if (filter === 'starred') return q.starred;
    if (PLATFORMS.includes(filter)) return q.platform === filter;
    return q.difficulty === filter;
  });

  return (
    <div className="app-container">
      <div className="max-width">
        <Header
          showForm={showForm}
          setShowForm={setShowForm}
          showExportMenu={showExportMenu}
          setShowExportMenu={setShowExportMenu}
          questions={questions}
          stats={stats}
          achievement={achievement}
          streak={streak}
          onClearAll={clearAllData}
        />

        {showForm && (
          <AddProblemForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={addQuestion}
            onCancel={() => setShowForm(false)}
          />
        )}

        <Filters filter={filter} setFilter={setFilter} stats={stats} />

        <div>
          {filteredQuestions.length === 0 ? (
            <EmptyState />
          ) : (
            filteredQuestions.map((q) => (
              <ProblemCard
                key={q.id}
                question={q}
                onToggleAttempted={toggleAttempted}
                onToggleStar={toggleStarred}
                onDelete={deleteQuestion}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default App;